import { useFetchDashboardOperationSummary } from '@hooks';
import loadable from '@loadable/component';
import { UIUtils } from '@utils';
import { pick } from 'lodash';
import React, { memo } from 'react';
import trans from 'translation';
import { KContainer } from 'uikit';

import Carousel from '../components/Carousel';
import DoughnutWithLegends from '../components/DoughnutWithLegends';
import Today from '../components/Today';
import { PopupName } from '../helpers';

const DriverLicense = loadable(() => import('./components/DriverLicense'));

const RegistrationEquipment = loadable(
  () => import('./components/RegistrationEquipment')
);

const Equipment = loadable(() => import('./components/Equipment'));

const onViewLicenseValidDriver = () => {
  UIUtils.popup.open({
    title: trans('license_valid_driver'),
    maxWidth: 'md',
    content: () => <DriverLicense name={PopupName.valid} />
  });
};

const onViewLicenseExpiredDriver = () => {
  UIUtils.popup.open({
    title: trans('license_expired_driver'),
    maxWidth: 'md',
    content: () => <DriverLicense name={PopupName.expired} />
  });
};

const onViewLicenseValidEquipment = () => {
  UIUtils.popup.open({
    title: trans('license_valid_equipment'),
    maxWidth: 'md',
    content: () => <RegistrationEquipment name={PopupName.valid} />
  });
};
const onViewRegistrationExpiredEquipment = () => {
  UIUtils.popup.open({
    title: trans('registration_expired_equipment'),
    maxWidth: 'md',
    content: () => <RegistrationEquipment name={PopupName.expired} />
  });
};
const onViewNoRegistrationEquipment = () => {
  UIUtils.popup.open({
    title: trans('no_registration_equipment'),
    maxWidth: 'md',
    content: () => <RegistrationEquipment name={PopupName.noRegistration} />
  });
};

const onViewGoodConditionEquipment = () => {
  UIUtils.popup.open({
    title: trans('in_good_condition_equipment'),
    maxWidth: 'md',
    content: () => <Equipment name={PopupName.goodCondition} />
  });
};
const onViewFaultyEquipment = () => {
  UIUtils.popup.open({
    title: trans('faulty_equipment'),
    maxWidth: 'md',
    content: () => <Equipment name={PopupName.faulty} />
  });
};
const onViewRepairEquipment = () => {
  UIUtils.popup.open({
    title: trans('in_repair_equipment'),
    maxWidth: 'md',
    content: () => <Equipment name={PopupName.inRepair} />
  });
};

const mappedLabels: Record<string, string> = {
  numOfNewJob: 'new',
  numOfActiveJob: 'active',
  numOfDraftJob: 'draft',
  numOfCloseJob: 'closed',
  numOfImportJob: 'import',
  numOfExportJob: 'export',
  numOfOthersJob: 'others',
  numOfBookedContainer: 'booked',
  numOfInvoicedContainer: 'invoiced',
  numOfDryContainer: 'dry',
  numOfReeferContainer: 'reefer',
  numOfHazardousContainer: 'hazardous',
  numOfNotReady: 'not_ready',
  numOfInTransit: 'in_transit',
  numOfReady: 'ready',
  numOfAllocated: 'allocated',
  numOfCompleted: 'completed',
  numOfCompletedContainer: 'completed',
  numOfInTransitContainer: 'in_transit',
  numOfCancelled: 'cancelled',
  numOfLicenseValid: 'license_valid',
  numOfLicenseExpired: 'license_expired',
  numOfMiscExpired: 'misc_expired',
  numOfMiscValid: 'misc_valid',

  // Equipment
  numOfEquipmentFaulty: 'faulty_equipment',
  numOfEquipmentInGoodCondition: 'in_good_condition_equipment',
  numOfEquipmentInRepair: 'in_repair_equipment',
  numOfEquipmentLicenseValid: 'license_valid_equipment',
  numOfEquipmentNoRegistration: 'no_registration_equipment',
  numOfEquipmentRegistrationExpired: 'registration_expired_equipment'
};

const mappedFunctions: Record<string, any> = {
  license_valid: onViewLicenseValidDriver,
  license_expired: onViewLicenseExpiredDriver,

  // Equipment
  license_valid_equipment: onViewLicenseValidEquipment,
  registration_expired_equipment: onViewRegistrationExpiredEquipment,
  no_registration_equipment: onViewNoRegistrationEquipment,
  in_good_condition_equipment: onViewGoodConditionEquipment,
  faulty_equipment: onViewFaultyEquipment,
  in_repair_equipment: onViewRepairEquipment
};

const Summary = () => {
  const {
    transportJobQuery: {
      data: transportJob,
      isInitialLoading: isTransportJobLoading
    },
    containerQuery: { data: container, isInitialLoading: isContainerLoading },
    driverQuery: { data: driver, isInitialLoading: isDriverLoading },
    tripQuery: { data: trip, isInitialLoading: isTripLoading },
    equipmentQuery: { data: equipment, isInitialLoading: isEquipmentLoading }
  } = useFetchDashboardOperationSummary();

  return (
    <Carousel.Container rightNode={<Today />}>
      <Carousel.Item title={trans('jobs')}>
        <KContainer.View
          dp="flex"
          row
          alignItems="center"
          justifyContent="space-between"
        >
          <KContainer.View dp="flex" width={'50%'} maxW={450}>
            <DoughnutWithLegends
              data={pick(transportJob, [
                'numOfNewJob',
                'numOfActiveJob',
                'numOfDraftJob',
                'numOfCloseJob'
              ])}
              mappedLabels={mappedLabels}
              isLoading={isTransportJobLoading}
            />
          </KContainer.View>
          <KContainer.View dp="flex" width={'50%'} maxW={450}>
            <DoughnutWithLegends
              data={pick(transportJob, [
                'numOfExportJob',
                'numOfImportJob',
                'numOfOthersJob'
              ])}
              mappedLabels={mappedLabels}
              isLoading={isTransportJobLoading}
            />
          </KContainer.View>
        </KContainer.View>
      </Carousel.Item>
      <Carousel.Item title={trans('container')}>
        <KContainer.View
          dp="flex"
          row
          alignItems="center"
          justifyContent="space-between"
        >
          <KContainer.View dp="flex" width={'50%'} maxW={450}>
            <DoughnutWithLegends
              data={pick(container, [
                'numOfBookedContainer',
                'numOfInTransitContainer',
                'numOfCompletedContainer',
                'numOfInvoicedContainer'
              ])}
              mappedLabels={mappedLabels}
              isLoading={isContainerLoading}
            />
          </KContainer.View>
          <KContainer.View dp="flex" width={'50%'} maxW={450}>
            <DoughnutWithLegends
              data={pick(container, [
                'numOfDryContainer',
                'numOfReeferContainer',
                'numOfHazardousContainer'
              ])}
              mappedLabels={mappedLabels}
              isLoading={isContainerLoading}
            />
          </KContainer.View>
        </KContainer.View>
      </Carousel.Item>
      <Carousel.Item
        title={trans('trips')}
        justifyContent="center"
        dp="flex"
        row
      >
        <DoughnutWithLegends
          data={trip}
          mappedLabels={mappedLabels}
          cols={2}
          isLoading={isTripLoading}
        />
      </Carousel.Item>
      <Carousel.Item title={trans('driver')}>
        <KContainer.View
          dp="flex"
          row
          alignItems="flex-end"
          justifyContent={'space-between'}
        >
          <KContainer.View dp="flex" width={'50%'} maxW={450}>
            <DoughnutWithLegends
              data={pick(driver, ['numOfLicenseExpired', 'numOfLicenseValid'])}
              mappedLabels={mappedLabels}
              mappedFunctions={mappedFunctions}
              isLoading={isDriverLoading}
            />
          </KContainer.View>
          <KContainer.View dp="flex" width={'50%'} maxW={450}>
            <DoughnutWithLegends
              data={pick(driver, ['numOfMiscExpired', 'numOfMiscValid'])}
              mappedLabels={mappedLabels}
              mappedFunctions={mappedFunctions}
              isLoading={isDriverLoading}
            />
          </KContainer.View>
        </KContainer.View>
      </Carousel.Item>
      <Carousel.Item title={trans('equipment')}>
        <KContainer.View
          dp="flex"
          row
          alignItems="flex-end"
          justifyContent="space-between"
        >
          <KContainer.View dp="flex" width={'50%'} maxW={450}>
            <DoughnutWithLegends
              data={pick(equipment, [
                'numOfEquipmentLicenseValid',
                'numOfEquipmentRegistrationExpired',
                'numOfEquipmentNoRegistration'
              ])}
              mappedLabels={mappedLabels}
              mappedFunctions={mappedFunctions}
              isLoading={isEquipmentLoading}
            />
          </KContainer.View>
          <KContainer.View dp="flex" width={'50%'} maxW={450}>
            <DoughnutWithLegends
              data={pick(equipment, [
                'numOfEquipmentInGoodCondition',
                'numOfEquipmentFaulty',
                'numOfEquipmentInRepair'
              ])}
              mappedLabels={mappedLabels}
              mappedFunctions={mappedFunctions}
              isLoading={isEquipmentLoading}
            />
          </KContainer.View>
        </KContainer.View>
      </Carousel.Item>
    </Carousel.Container>
  );
};

export default memo(Summary);
